import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Banner.css";
import IconButton from "@mui/material/IconButton";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Modal,
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { API_BASE } from "../utils/api";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

const Banner = () => {
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [banners, setBanners] = useState([]);
  const [selectedBanner, setSelectedBanner] = useState(null);

  const [bannerData, setBannerData] = useState({
    bannerImage: null,
    startDate: "", 
    endDate: "",
    description: "",
    imagePath: "",
    visible: true,
    previewUrl: "",
  });

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/banners/banner`);
        setBanners(response.data);
      } catch (error) {
        console.error("Error fetching banners:", error);
      }
    };
    fetchBanners();
  }, []);

  const toLocalDateTimeInput = (dateLike) => {
    if (!dateLike) return "";
    const d = new Date(dateLike);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const YYYY = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const DD = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${YYYY}-${MM}-${DD}T${hh}:${mm}`;
  };

  const toISOStringFromLocalInput = (localValue) => {
    if (!localValue) return "";
    const d = new Date(localValue);
    return d.toISOString();
  };

  const handleRowDoubleClick = (banner) => {
    setSelectedBanner(banner);
    setBannerData({
      startDate: banner.startDate ? toLocalDateTimeInput(banner.startDate) : "",
      endDate: banner.endDate ? toLocalDateTimeInput(banner.endDate) : "",
      description: banner.description || "",
      imagePath: banner.imagePath || "",
      visible: typeof banner.visible === "boolean" ? banner.visible : true,
      bannerImage: null,
      previewUrl: banner.imagePath ? `${API_BASE}/${banner.imagePath}` : "",
    });
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setBannerData((prevData) => ({
      ...prevData,
      bannerImage: file || null,
      imagePath: file ? "" : prevData.imagePath,
      previewUrl: file ? URL.createObjectURL(file) : prevData.previewUrl,
    }));
  };

  const handleChange = (field, value) => {
    setBannerData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Validation
    if (!bannerData.bannerImage && !bannerData.imagePath) {
      toast.error("Please upload a banner image.");
      return;
    }
    if (!bannerData.startDate) {
      toast.error("Please select a start date and time.");
      return;
    }
    if (!bannerData.endDate) {
      toast.error("Please select an end date and time.");
      return;
    }

    const formData = new FormData();
    if (bannerData.bannerImage) {
      formData.append("bannerImage", bannerData.bannerImage);
    }
    // convert local inputs to ISO
    formData.append(
      "startDate",
      bannerData.startDate ? toISOStringFromLocalInput(bannerData.startDate) : ""
    );
    formData.append(
      "endDate",
      bannerData.endDate ? toISOStringFromLocalInput(bannerData.endDate) : ""
    );
    formData.append("description", bannerData.description || "");
    formData.append("visible", bannerData.visible);

    try {
      if (selectedBanner) {
        await axios.put(
          `${API_BASE}/api/banners/${selectedBanner._id}`,
          formData
        );
        toast.success("Banner updated successfully!");
      } else {
        await axios.post(`${API_BASE}/api/banners/upload`, formData);
        toast.success("Banner uploaded successfully!");
      }

      setShowForm(false);
      setShowModal(false);
      window.location.reload();
    } catch (error) {
      console.error("Error saving banner:", error);
      toast.error("Failed to save banner. Please try again.");
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedBanner(null);
    setBannerData({
      bannerImage: null,
      startDate: "",
      endDate: "",
      description: "",
      imagePath: "",
      visible: true,
      previewUrl: "",
    });
  };

  const responsiveColumnHeader = (header, field, customRenderer) => (rowData) => (
    <div className="responsive-cell">
      <span className="responsive-header">{header}</span>
      <span className="responsive-value">
        {customRenderer ? customRenderer(rowData) : rowData[field]}
      </span>
    </div>
  );

  // Table templates
  const imageTemplate = (rowData) => (
    <img
      src={`${API_BASE}/${rowData.imagePath}`}
      alt="Banner"
      className="banner-img"
      style={{ width: "100px" }}
    />
  );

  const dateTemplate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleString();
  };

  const statusTemplate = (rowData) => {
    const now = new Date();
    const start = new Date(rowData.startDate);
    const end = new Date(rowData.endDate);

    let status = "Active";
    if (now < start) status = "Upcoming";
    else if (now > end) status = "Expired";

    return <span className={`status-badge ${status.toLowerCase()}`}>{status}</span>;
  };

  return (
    <div>
      <ToastContainer position="top-center" autoClose={3000} />
      {showForm && (
        <div
          className="form-header"
          style={{ display: "flex", alignItems: "center", gap: "10px" }}
        >
          <IconButton
            onClick={() => setShowForm(false)}
            className="back-button"
            style={{
              backgroundColor: "#e0e0e0",
              border: "1px solid #ccc",
              borderRadius: "50%",
              padding: "5px",
              marginTop: "20px",
              color: "#333",
            }}
          >
            <KeyboardBackspaceIcon />
          </IconButton>
          <h5 className="heading">Add Banner</h5>
        </div>
      )}

      {!showForm ? (
        <>
          <h1>Banner</h1>
          <div>
            <button
              className="buttonStyle"
              style={{ marginBottom: "16px" }}
              onClick={() => {
                setShowForm(true);
                handleClose();
              }}
            >
              Create New Banner
            </button>

            <DataTable
              value={banners}
              paginator
              rows={10}
              scrollable
              scrollHeight="500px"
              className="datatable-scrollable"
              onRowDoubleClick={(e) => handleRowDoubleClick(e.data)}
              onRowClick={(e) => handleRowDoubleClick(e.data)}
              responsiveLayout="scroll"
            >
              <Column
                header="Image"
                body={responsiveColumnHeader("Image", "imagePath", imageTemplate)}
                style={{ width: "120px" }}
              />
              <Column
                field="startDate"
                header="Start Date"
                body={responsiveColumnHeader("Start Date", "startDate", (row) =>
                  dateTemplate(row.startDate)
                )}
              />
              <Column
                field="endDate"
                header="End Date"
                body={responsiveColumnHeader("End Date", "endDate", (row) =>
                  dateTemplate(row.endDate)
                )}
              />
              <Column
                field="description"
                header="Description"
                body={responsiveColumnHeader("Description", "description")}
              />
              <Column
                header="Status"
                body={responsiveColumnHeader("Status", "status", statusTemplate)}
              />
            </DataTable>
          </div>
        </>
      ) : (
        <div>
          <form onSubmit={handleSubmit} className="forms">
            <label className="label">Upload Image:</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="input"
            />
            {bannerData.previewUrl && (
              <img
                src={bannerData.previewUrl}
                alt="Preview"
                className="banner-img"
                style={{ margin: "10px 0" }}
              />
            )}

            <label className="label">Start Date & Time:</label>
            <input
              type="datetime-local"
              value={bannerData.startDate}
              onChange={(e) => handleChange("startDate", e.target.value)}
              className="input"
              placeholder="Select Start Date & Time"
            />

            <label className="label">End Date & Time:</label>
            <input
              type="datetime-local"
              value={bannerData.endDate}
              onChange={(e) => handleChange("endDate", e.target.value)}
              className="input"
              placeholder="Select End Date & Time"
            />

            <label className="label">Description:</label>
            <input
              type="text"
              value={bannerData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              className="input"
            />

            <button type="submit" className="buttons">
              Submit
            </button>
          </form>
        </div>
      )}

      <Modal
        open={showModal}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleClose();
        }}
      >

        <Box className="modal-box">
          <CloseIcon onClick={handleClose} className="close-icon" />
          <Typography variant="h6">Edit Banner</Typography>

          <label className="label">Start Date & Time:</label>
          <input
            type="datetime-local"
            value={bannerData.startDate}
            onChange={(e) => handleChange("startDate", e.target.value)}
            className="input calendar-field"
            placeholder="Select Start Date & Time"
          />

          <label className="label">End Date & Time:</label>
          <input
            type="datetime-local"
            value={bannerData.endDate}
            onChange={(e) => handleChange("endDate", e.target.value)}
            className="input calendar-field"
            placeholder="Select End Date & Time"
          />

          <label className="label">Description:</label>
          <input
            type="text"
            value={bannerData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            className="input"
          />

          <input type="file" accept="image/*" onChange={handleImageChange} />
          {bannerData.previewUrl && (
            <img
              src={bannerData.previewUrl}
              alt="Preview"
              className="banner-img"
              style={{ margin: "10px 0" }}
            />
          )}
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={bannerData.visible}
                  onChange={(e) => handleChange("visible", e.target.checked)}
                />
              }
              label="Visible"
            />
          </div>
          <Button variant="contained" onClick={handleSubmit}>
            Submit
          </Button>
        </Box>
      </Modal>
    </div>
  );
};

export default Banner;
